import type { InitOptions, Resource } from 'i18next'

import { TabSyncManager } from '@peerup/tab-sync'
import { CookieStorage } from '@peerup/utils'
import i18next from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'

import type { I18nConfig, I18nEvents, Meta, Renderer, SupportedLocale } from './types'

import { I18nEventType } from './types'

// 导出类型
export * from './createI18n'
export * from './loaders'
export { EnvTag, I18nEventType } from './types'
export type {
  CreateI18nOptions,
  I18nConfig,
  I18nEvents,
  I18nHandle,
  I18nProxy,
  Meta,
  Renderer,
  SupportedLocale,
} from './types'

/**
 * I18nKit 国际化工具类
 * 1. 支持多语言
 */
class I18nKit {
  private renderers: Record<string, Renderer> = {}
  /** 多页签同步管理器 */
  public tabSync: TabSyncManager<I18nEvents> | null = null
  private storage = new CookieStorage({ namespace: 'i18n_kit' })
  /** 认证配置 */
  private readonly config: I18nConfig
  private cookieOptions = {}
  // 存储当前的定时器ID
  private reloadTimerId: number | null = null

  constructor(config: I18nConfig = {}) {
    const {
      defaultLocale = 'zh-CN',
      fallbackLocale = 'zh-CN',
      backend = {
        loadPath: (languages: string[]) => `/src/locales/${languages[0]}.json`,
      },
      detection = { order: ['localStorage', 'navigator'] }, // 自动检测浏览器语言
      debug = false,
      resources,
      // 初始化时接收渲染器配置
      renderers = {},
    } = config

    // 存储渲染器
    this.renderers = renderers
    this.config = config
    this.cookieOptions = { domain: this.config.cookieDomain || CookieStorage.getTopDomain() }

    // 配置后端，处理嵌套的响应结构
    const backendOptions = {
      ...backend,
      // 自定义解析函数，提取 data 字段
      parse: (data: string) => {
        try {
          const parsedData = JSON.parse(data)
          const inner = (parsedData as Record<string, unknown>)?.data
          // 仅当 data 字段是对象（API 包裹格式）时解包，避免扁平词条文件中
          // 恰好存在 "data" 字符串词条时把整个词条对象替换成字符串
          if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
            return inner
          }
          return parsedData
        } catch (e) {
          console.error('解析语言包数据失败', e)
          return {} // 出错时返回空对象
        }
      },
      // 可选：添加缓存控制
      fetchOptions: {
        cache: 'no-cache', // 使用浏览器默认缓存
      },
    }

    const initOptions: InitOptions = {
      defaultNS: 'translation',
      lng: defaultLocale,
      fallbackLng: fallbackLocale,
      load: 'currentOnly', // 只加载当前完整语言代码，不做语言层级回退
      backend: backendOptions,
      detection,
      debug,
      resources: resources as Resource,
      interpolation: {
        skipOnVariables: false,
        escapeValue: false,
        prefix: '${',
        suffix: '}',
      },
    }

    i18next
      .use(HttpBackend)
      .use(LanguageDetector)
      .init(initOptions)
    this.initTabSync()
    document.documentElement.lang = defaultLocale || fallbackLocale
  }

  /** 环境标签 */
  get envTag() {
    return typeof this.config.envTag === 'function' ? this.config.envTag() : this.config.envTag
  }

  // 添加渲染器
  addRenderer(name: string, renderer: Renderer): void {
    this.renderers[name] = renderer
  }

  // 获取渲染器
  getRenderer(name: string): Renderer | undefined {
    return this.renderers[name]
  }

  // 初始化多页签同步管理器
  private initTabSync(): void {
    this.tabSync = new TabSyncManager({
      channel: `i18n-sync-${this.envTag}`,
      crossDomainConfig: {
        staticOrigin: this.config.staticOrigin,
        targetOrigin: [`*.${CookieStorage.getTopDomain()}`],
        scope: `i18n-kit-${this.envTag}`,
      },
    })
    this.setupTabSync()
  }

  /**
   * 设置标签页同步
   */
  private setupTabSync = () => {
    if (!this.tabSync) return

    // 注册所有事件处理器
    this.tabSync.on(I18nEventType.LANGUAGE_CHANGED, this.handleLanguageChangedSync)
  }

  /**
   * 语言切换
   */
  private handleLanguageChangedSync = (data: I18nEvents[I18nEventType.LANGUAGE_CHANGED]) => {
    const { language } = data
    if (language === this.getCurrentLanguage()) return
    if (this.config.onLanguageChange) {
      this.config.onLanguageChange(language)
    } else {
      window.location.reload()
    }
  }

  /**
   * 设置token cookie, 兼容逻辑
   */
  private setLanguageCookie = (language: string) => {
    const envTag = this.envTag
    if (!envTag) return
    this.storage.set(envTag, language, {
      ...this.cookieOptions,
      sameSite: 'none',
      expires: 30,
      path: '/',
    })
  }

  // 切换语言
  setLocale(locale: SupportedLocale): void {
    // 清除之前可能存在的定时器
    if (this.reloadTimerId !== null) {
      clearTimeout(this.reloadTimerId)
      this.reloadTimerId = null
    }

    this.setLanguageCookie(locale)
    this.tabSync?.send(I18nEventType.LANGUAGE_CHANGED, { language: locale })
    this.reloadTimerId = window.setTimeout(() => {
      this.config.onLanguageChange?.(locale)
    }, 500)
  }

  // 增强的翻译方法，支持嵌套的国际化键值和数组渲染
  $t(key: string, data: Record<string, unknown> | string = {}, fallback?: string): string {
    if (!i18next.isInitialized) {
      console.warn('i18n instance not initialized yet!')
      return typeof data === 'string' ? data : key
    }

    // 处理回退值
    let processedData: Record<string, unknown> = {}
    let fallbackValue: string | undefined

    if (typeof data === 'string') {
      fallbackValue = data
    } else {
      // 保持原始数据结构
      processedData = data as Record<string, unknown>
      fallbackValue = fallback
    }

    if (!key) {
      return fallbackValue ?? ''
    }

    if (!this.hasTranslation(key)) {
      console.warn(`Translation key "${key}" not found`)
      return fallbackValue ?? key
    }

    try {
      // 获取原始翻译文本
      const rawText = i18next.getResource(i18next.language, 'translation', key) as string

      // 处理自定义渲染器，支持多层数据访问和元数据
      const result = rawText.replace(/\$\{__(\w+)\(([\w\s,.]+)\)\}/g, (match, rendererName, path) => {
        const renderer = this.renderers[`__${rendererName}`]
        if (renderer) {
          // 解析路径和元数据
          const [valuePath, metaPath] = path.split(',').map((p: string) => p.trim())

          // 获取值
          const value = this.getValueByPath(processedData, valuePath)
          if (value === undefined) {
            console.warn(`Value for path "${valuePath}" is undefined in data:`, processedData)
            return match
          }

          // 获取元数据（如果存在）
          const meta = metaPath ? this.getValueByPath(processedData, metaPath) : undefined

          // 调用渲染器
          try {
            const result = renderer(value, meta as Meta)
            return typeof result === 'string' ? result : result.outerHTML
          } catch (error) {
            console.error(`Error in renderer ${rendererName}:`, error)
            return match
          }
        }
        console.warn(`Renderer ${rendererName} not found`)
        return match
      }).replace(/\$\{([\w.]+)\}/g, (match, path) => {
        const value = this.getValueByPath(processedData, path)
        return value === undefined ? match : String(value)
      })

      return result
    } catch (error) {
      console.error(`Translation error for key "${key}":`, error)
      return fallbackValue ?? key
    }
  }

  // 添加工具方法来获取嵌套值
  private getValueByPath(obj: Record<string, unknown>, path: string): unknown | undefined {
    if (!obj || !path) {
      return undefined
    }

    let current: unknown = obj
    for (const part of path.split('.')) {
      if (current === null || current === undefined) {
        return undefined
      }
      if (typeof current !== 'object') {
        return undefined
      }
      current = (current as Record<string, unknown>)[part]
    }
    return current
  }

  // 添加资源
  // eslint-disable-next-line ts/no-explicit-any
  addResources(lng: string, namespace: string, resources: any): void {
    i18next.addResources(lng, namespace, resources)
  }

  // 监听语言切换事件
  onLanguageChange(callback: (lng: string) => void): () => void {
    const handler = (lng: string) => callback(lng)
    i18next.on('languageChanged', handler)
    return () => i18next.off('languageChanged', handler)
  }

  // 获取当前语言
  getCurrentLanguage(): string {
    return i18next.language
  }

  // 获取 i18next 实例，以便进行高级配置
  getI18next() {
    return i18next
  }

  // 添加新的实用方法
  hasTranslation(key: string, lng?: string): boolean {
    return i18next.exists(key, { lng })
  }
}

export default I18nKit
